// frontend/src/features/orders/lib/orderTimeline.js
//
// Project a single Order object into a role-appropriate timeline.
// Order-item state is read from the acceptance and fulfillment axes.
import { PAYMENT_MESSAGES } from '../paymentCopy.js';
//
// Return shape (consumed by OrderStatusTimeline.jsx):
//   { stage, tone, label, hint, steps[], branch|null, notes[], itemsKnown }

// ---- customer-facing stage catalogue ----
const CUSTOMER_STAGE = {
  awaiting_payment:      { tone: 'warning', label: 'در انتظار پرداخت',         hint: 'برای ادامه، سفارش را پرداخت کنید.' },
  cheque_registration:   { tone: 'warning', label: 'ثبت اطلاعات چک',           hint: 'اطلاعات چک‌ها را در پنل وارد کنید.' },
  cheque_confirmation:   { tone: 'warning', label: 'در انتظار تأیید چک',       hint: 'در انتظار تأیید فروشنده هستیم.' },
  awaiting_seller:       { tone: 'info',    label: 'در انتظار تأیید فروشنده',  hint: 'سفارش برای بررسی به فروشنده ارسال شد.' },
  preparing:             { tone: 'info',    label: 'در حال آماده‌سازی',        hint: 'فروشنده در حال آماده‌سازی سفارش است.' },
  partially_shipped:     { tone: 'info',    label: 'بخشی ارسال شده',           hint: 'بخشی از اقلام ارسال شده است.' },
  shipped:               { tone: 'info',    label: 'ارسال شده',                hint: 'سفارش در مسیر تحویل است.' },
  delivered:             { tone: 'success', label: 'تحویل داده شد',            hint: 'سفارش با موفقیت تحویل شد.' },
  rejected:              { tone: 'danger',  label: 'رد شده توسط فروشنده',      hint: 'سفارش توسط فروشنده رد شد.' },
  cancelled:             { tone: 'muted',   label: 'لغو شده',                  hint: 'این سفارش لغو شده است.' },
  refund_due:            { tone: 'warning', label: 'بازپرداخت در انتظار',      hint: 'بازپرداخت مبلغ در انتظار انجام است.' },
};

// ---- seller / platform stage catalogue (reuses the same icon keys) ----
const SELLER_STAGE = {
  awaiting_seller:       { tone: 'warning', label: 'در انتظار تأیید شما',      hint: 'این اقلام در انتظار تأیید شما هستند.' },
  preparing:             { tone: 'info',    label: 'در حال آماده‌سازی',        hint: 'اقلام تأیید شده‌اند و باید آماده شوند.' },
  partially_shipped:     { tone: 'info',    label: 'بخشی ارسال شده',           hint: 'بخشی از اقلام ارسال شده است.' },
  shipped:               { tone: 'info',    label: 'ارسال شده',                hint: 'اقلام تحویل پست شده‌اند.' },
  delivered:             { tone: 'success', label: 'تحویل داده شده',           hint: 'اقلام تحویل داده شدند.' },
  rejected:              { tone: 'danger',  label: 'رد شده',                   hint: 'این اقلام رد شده‌اند.' },
  cancelled:             { tone: 'muted',   label: 'لغو شده',                  hint: 'این سفارش لغو شده است.' },
};

const CUSTOMER_STEPS = {
  placed: 'ثبت سفارش', paid: 'پرداخت', accepted: 'تأیید فروشنده', shipped: 'ارسال', delivered: 'تحویل',
};
const SELLER_STEPS = {
  placed: 'ثبت سفارش', paid: 'پرداخت', accepted: 'تأیید', shipped: 'ارسال', delivered: 'تحویل',
};

// --- helpers -------------------------------------------------------------

// "Is this item accepted?"
function isAccepted(item) {
  return item.vendor_acceptance_status === 'accepted';
}

function isHandedToCarrier(item) {
  return ['handed_to_carrier', 'delivered'].includes(item.vendor_fulfillment_status);
}

function isDelivered(item) {
  return item.vendor_fulfillment_status === 'delivered';
}

function isCancelledItem(item) {
  return ['rejected', 'auto_cancelled'].includes(item.vendor_acceptance_status);
}

function formatPersianCount(value) {
  return new Intl.NumberFormat('fa-IR').format(value);
}

// --- customer projection --------------------------------------------------

function buildCustomerTimeline(order) {
  const items = order.items ?? [];
  const alive = items.filter(i => !isCancelledItem(i));
  const allRejected = items.length > 0 && alive.length === 0;
  const anyHanded = alive.some(isHandedToCarrier);
  const allHanded = alive.length > 0 && alive.every(isHandedToCarrier);
  const allDelivered = alive.length > 0 && alive.every(isDelivered);
  const handedItems = alive.filter(isHandedToCarrier);
  const handedDates = handedItems
    .map(item => item.shipped_at ?? item.handed_to_carrier_at)
    .filter(Boolean)
    .sort((left, right) => new Date(left) - new Date(right));

  let stage = 'awaiting_payment';
  let paid = order.payment_status === 'paid';

  if (order.payment_status === 'cancelled_with_refund_due') {
    stage = 'refund_due';
  } else if (order.delivery_status === 'cancelled') {
    stage = 'cancelled';
  } else if (!paid && order.cheque_status === 'awaiting_registration') {
    stage = 'cheque_registration';
  } else if (!paid && order.cheque_status === 'awaiting_confirmation') {
    stage = 'cheque_confirmation';
  } else if (!paid) {
    stage = 'awaiting_payment';
  } else if (allRejected) {
    stage = 'rejected';
  } else if (order.delivery_status === 'delivered') {
    stage = 'delivered';
  } else if (allDelivered) {
    stage = 'delivered';
  } else if (allHanded) {
    stage = 'shipped';
  } else if (anyHanded) {
    stage = 'partially_shipped';
  } else if (alive.some(i => i.vendor_acceptance_status === 'pending')) {
    stage = 'awaiting_seller';
  } else {
    stage = 'preparing';
  }

  const steps = [
    { key: 'placed',    label: CUSTOMER_STEPS.placed,    state: 'done', date: order.created_at },
    { key: 'paid',      label: CUSTOMER_STEPS.paid,      state:
        stage === 'cancelled' ? 'stopped' : paid ? 'done' : 'current',
      detail: !paid
        ? order.cheque_status === 'awaiting_registration'
          ? 'در انتظار ثبت چک'
          : order.cheque_status
            ? 'در انتظار تأیید چک'
            : 'در انتظار پرداخت'
        : undefined },
    { key: 'accepted',  label: CUSTOMER_STEPS.accepted,  state:
        ['cancelled', 'rejected'].includes(stage) ? 'stopped'
        : stage === 'awaiting_seller' && !alive.some(isAccepted) ? 'current'
        : alive.some(isAccepted)
          || paid && ['preparing','partially_shipped','shipped','delivered'].includes(stage) ? 'done'
        : 'pending' },
    { key: 'shipped',   label: CUSTOMER_STEPS.shipped,   state:
        ['cancelled', 'rejected'].includes(stage) ? 'stopped'
        : allHanded ? 'done'
        : anyHanded || stage === 'preparing' || stage === 'shipped' ? 'current'
        : 'pending',
      date: handedDates[0],
      detail: anyHanded && !allHanded
        ? `${formatPersianCount(handedItems.length)} از ${formatPersianCount(alive.length)} کالا`
        : undefined },
    {
      key: 'delivered',
      label: CUSTOMER_STEPS.delivered,
      state: ['cancelled', 'rejected'].includes(stage)
        ? 'stopped'
        : allDelivered || stage === 'delivered' ? 'done' : 'pending',
      date: order.delivered_at,
    },
  ];

  let branch = null;
  if (stage === 'cancelled')  branch = { key: 'cancelled',  tone: 'muted',   label: 'سفارش لغو شده' };
  if (stage === 'rejected')   branch = { key: 'rejected',   tone: 'danger',  label: 'رد شده توسط فروشنده' };
  if (stage === 'refund_due') branch = { key: 'refund_due', tone: 'warning', label: 'بازپرداخت در انتظار' };

  const notes = [];
  if (order.secure_status === 'awaiting_receipt') {
    notes.push({
      key: 'secure_holding',
      tone: 'warning',
      text: PAYMENT_MESSAGES.secureHoldingNotice,
    });
  }
  const rejectedCount = items.filter(
    item => item.vendor_acceptance_status === 'rejected',
  ).length;
  if (rejectedCount > 0 && rejectedCount < items.length) {
    notes.push({
      key: 'partial_rejected',
      tone: 'warning',
      text: `${formatPersianCount(rejectedCount)} کالا توسط فروشنده رد شده است.`,
    });
  }
  if (order.cheque_status === 'awaiting_registration') {
    notes.push({ key: 'cheque-prepay', tone: 'warning', text: 'برای فعالسازی سفارش، اطلاعات چک‌ها را ثبت کنید.' });
  }

  const meta = CUSTOMER_STAGE[stage] ?? CUSTOMER_STAGE.awaiting_payment;
  return { stage, tone: meta.tone, label: meta.label, hint: meta.hint,
           steps, branch, notes, itemsKnown: items.length > 0 };
}

// --- seller / platform projection ----------------------------------------

// Precedence for the seller badge (documented decision):
//   1. orders.delivery_status === 'cancelled' always wins — an order-level
//      cancellation supersedes whatever the vendor item axes say.
//   2. Otherwise the vendor-scoped item axes
//      (order_items.vendor_acceptance_status / vendor_fulfillment_status)
//      are the source of truth. A global 'shipped'/'delivered' on the order
//      does NOT override this vendor's items unless the vendor has no items
//      in this order (platform-only orders fall back to the order status).
// Keep order-level writes consistent with the item axes on the backend
// (see app/services/order_status_transitions.py) so both views agree.
function buildSellerTimeline(order) {
  const items = order.items ?? [];
  const isCancelled = order.delivery_status === 'cancelled';
  const awaitingCheque = order.cheque_status === 'awaiting_confirmation';

  const hasPending  = items.some(i => i.vendor_acceptance_status === 'pending');
  const hasAccepted = items.some(isAccepted);
  const anyHanded   = items.some(isHandedToCarrier);
  const allHanded   = items.length > 0 && items.every(isHandedToCarrier);
  const allDelivered = items.length > 0 && items.every(isDelivered);
  const allRejected = items.length > 0 && items.every(isCancelledItem);

  // Reuse customer stage keys so the existing STAGE_ICONS map keeps working.
  let stage = 'awaiting_seller';
  if (isCancelled || order.delivery_status === 'cancelled') stage = 'cancelled';
  else if (!items.length && order.delivery_status === 'delivered') stage = 'delivered';
  else if (!items.length && order.delivery_status === 'shipped') stage = 'shipped';
  else if (allRejected) stage = 'rejected';
  else if (allDelivered) stage = 'delivered';
  else if (anyHanded && !allHanded) stage = 'partially_shipped';
  else if (allHanded) stage = 'shipped';
  else if (hasPending && !hasAccepted && !awaitingCheque) stage = 'awaiting_seller';
  else if (hasAccepted) stage = 'preparing';
  else stage = 'awaiting_seller';

  const steps = [
    { key: 'placed',    label: SELLER_STEPS.placed,    state: 'done', date: order.created_at },
    { key: 'paid',      label: SELLER_STEPS.paid,      state: order.payment_status === 'paid' ? 'done' : 'current' },
    { key: 'accepted',  label: SELLER_STEPS.accepted,
      state: stage === 'awaiting_seller' ? 'current' : stage === 'rejected' ? 'stopped' : 'done' },
    { key: 'shipped',   label: SELLER_STEPS.shipped,
      state: allHanded ? 'done' : anyHanded ? 'current' : 'pending' },
    { key: 'delivered', label: SELLER_STEPS.delivered, state: allDelivered ? 'done' : 'pending' },
  ];

  let branch = null;
  if (stage === 'cancelled') branch = { key: 'cancelled', tone: 'muted',  label: 'سفارش لغو شده' };
  if (stage === 'rejected')  branch = { key: 'rejected',  tone: 'danger', label: 'رد شده' };

  const notes = [];
  if (awaitingCheque) {
    notes.push({ key: 'cheque-pending', tone: 'warning', text: 'تا تأیید چک‌ها امکان ارسال وجود ندارد.' });
  }

  const meta = SELLER_STAGE[stage] ?? SELLER_STAGE.preparing;
  return { stage, tone: meta.tone, label: meta.label, hint: meta.hint,
           steps, branch, notes, itemsKnown: items.length > 0 };
}

// --- public API ----------------------------------------------------------

export function buildOrderTimeline(order, { role = 'customer' } = {}) {
  if (!order) {
    return { stage: 'cancelled', tone: 'muted', label: '—', hint: '',
             steps: [], branch: null, notes: [], itemsKnown: false };
  }
  if (role === 'customer') return buildCustomerTimeline(order);
  return buildSellerTimeline(order);
}